import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import StatueScrub from "./StatueScrub";
import vinesAvif from "../assets/hero-vines-1774.avif";
import vinesWebp from "../assets/hero-vines-1774.webp";
import vinesMobAvif from "../assets/hero-vines-1280.avif";
import vinesMobWebp from "../assets/hero-vines-1280.webp";
import posterFinal from "../assets/poster-final-720.webp";
import BookCTA from "./BookCTA";
import "./Hero.css";

/* ---- scrub timing (experiment here) ----
   SCRUB_SPAN: fraction of the hero's own height over which the animation
   plays out as the hero scrolls away. 0.10 = the statue finishes once you
   have scrolled 10% of the hero past the top of the window.
   The hero is not pinned, so the page moves from the very first pixel and
   page motion and statue motion always overlap. Raise this for a slower,
   longer scrub; lower it for a snappier one. */
const SCRUB_SPAN = 0.3;

/* ---- hero spacing (experiment here) ----
   Any CSS length works, including clamp()/calc(), so a value can scale with
   the window. The hero's height is content + these two paddings, so raising
   SPACE_TOP makes the hero taller without opening a gap under the statue.

   SPACE_TOP     headroom between the nav bar and the composition. This is
                 what gives the hero its stature, and how much of the
                 circuit-vine artwork reads above the headline.
   SPACE_BOTTOM  gap under the statue before the next section begins.
                 Keep this small — it was the "empty bottom" problem.
   The _MOBILE pair does the same job at <= 700px, where the hero is a
   full-viewport stacked composition instead of two columns. On phones
   SPACE_TOP_MOBILE is now the ONLY control over the gap above the
   "AI Products · Strategic Consulting" line — nothing else pads it. */
const SPACE_TOP = "clamp(2rem, 20vh, 20rem)";
const SPACE_BOTTOM = "5rem";
const SPACE_TOP_MOBILE = "1rem";
const SPACE_BOTTOM_MOBILE = "0rem";

/* Phone-only size for the "We build custom AI products…" line.
   Desktop keeps --body-lg (1.1875rem). */
const SUB_SIZE_MOBILE = "1rem";

const imageSet = (avif, webp) =>
  `image-set(url("${avif}") type("image/avif"), url("${webp}") type("image/webp"))`;

const heroVars = {
  "--hero-vines": imageSet(vinesAvif, vinesWebp),
  "--hero-vines-mobile": imageSet(vinesMobAvif, vinesMobWebp),
  "--hero-space-top": SPACE_TOP,
  "--hero-space-bottom": SPACE_BOTTOM,
  "--hero-space-top-m": SPACE_TOP_MOBILE,
  "--hero-space-bottom-m": SPACE_BOTTOM_MOBILE,
  "--hero-sub-size-m": SUB_SIZE_MOBILE,
};

/**
 * Sticky scroll-scrubbed hero (brief §7–§10).
 *
 * The hero is content-height and unpinned; the statue completes within
 * the first SCRUB_SPAN of the hero scrolling away via StatueScrub's
 * progressRef — zero React re-renders per frame.
 *
 * prefers-reduced-motion collapses the region to one viewport and shows the
 * final frame as a static image.
 */
const Hero = () => {
  const region = useRef(null);
  const progress = useRef(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => {
    if (reduced) return undefined;
    const onScroll = () => {
      const el = region.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      /* 0 at page top -> 1 after SCRUB_SPAN of the hero scroll. */
      const span = r.height * SCRUB_SPAN;
      progress.current = span > 0 ? Math.min(1, Math.max(0, -r.top / span)) : 1;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [reduced]);

  return (
    <section
      className={`hero ${reduced ? "hero--static" : ""}`}
      aria-label="EffiGen — engineering intelligence"
      style={heroVars}
    >
      <div className="hero-scroll" ref={region}>
        <div className="hero-sticky">
          <div className="hero-vines" aria-hidden="true" />
          <div className="hero-vignette" aria-hidden="true" />

          <div className="hero-layout container">
            <div className="hero-copy">
              <p className="eyebrow">
                AI Products · Strategic Consulting
                <span className="hero-eyebrow-extra"> · AI Training</span>
              </p>
              <h1>
                Engineering Intelligence.
                <br />
                Building Impact.
              </h1>
              <p className="hero-sub">
                We build custom AI products, provide AI consulting, and offer AI
                training.
              </p>
              <div className="hero-ctas">
                <BookCTA ctaLocation="hero" className="btn-primary">
                  Book a Free Consultation
                </BookCTA>
                <Link to="/services" className="link-arrow">
                  Explore Services <span className="arrow">→</span>
                </Link>
              </div>
            </div>

            <div className="hero-statue-slot">
              {reduced ? (
                <img
                  className="hero-statue-static"
                  src={posterFinal}
                  alt=""
                  aria-hidden="true"
                />
              ) : (
                <StatueScrub className="hero-statue" progressRef={progress} />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
